import { useState, useEffect } from 'react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import '../components/Layout.css';
import './Dashboard.css';

const STATUS_COLORS = { pending:'#F59E0B', assigned:'#3B82F6', picked_up:'#8B5CF6', delivered:'#10B981', cancelled:'#EF4444' };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/analytics/dashboard')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="empty-state"><div className="icon">⏳</div>Loading dashboard…</div>;
  if (!data)   return <div className="empty-state"><div className="icon">⚠️</div>Failed to load dashboard.</div>;

  const { totals, weeklyChart, byStatus, topDrivers } = data;

  const STATS = [
    { icon: '📦', label: 'Total Orders', value: totals.total_orders, sub: `${totals.today_orders} today` },
    { icon: '✅', label: 'Delivered', value: totals.delivered_orders, sub: `${totals.delivery_rate}% rate` },
    { icon: '🏍️', label: 'Active Drivers', value: totals.active_drivers, sub: 'on duty' },
    { icon: '🏪', label: 'Stores', value: totals.total_stores, sub: 'locations' },
  ];

  const pieData = byStatus.map(s => ({ name: s.status, value: Number(s.count) }));

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Overview of your delivery operations</p>
        </div>
      </div>

      {/* STATS */}
      <div className="stats-row">
        {STATS.map((s, i) => (
          <div className="stat-card" key={i}>
            <span className="stat-card__icon">{s.icon}</span>
            <div className="stat-card__label">{s.label}</div>
            <div className="stat-card__value">{s.value ?? 0}</div>
            <div className="stat-card__sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="dash-grid">
        {/* WEEKLY CHART */}
        <div className="card dash-chart">
          <h3 className="card-title">Orders — Last 7 Days</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={weeklyChart} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0057FF" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#0057FF" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F5" />
              <XAxis dataKey="date" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#0057FF" strokeWidth={2} fill="url(#cg)" name="Orders" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* STATUS PIE */}
        <div className="card dash-pie">
          <h3 className="card-title">Orders by Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={STATUS_COLORS[entry.name] || '#CBD5E1'} />
                ))}
              </Pie>
              <Legend iconType="circle" iconSize={10} formatter={v => <span style={{ fontSize:12, textTransform:'capitalize' }}>{v}</span>} />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* TOP DRIVERS */}
        <div className="card dash-drivers">
          <h3 className="card-title">Top Drivers</h3>
          {topDrivers.length === 0
            ? <div className="empty-state" style={{ padding:'40px 0' }}><div className="icon">🏍️</div>No data yet</div>
            : <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topDrivers} layout="vertical" margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F5" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize:11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize:11 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="deliveries" fill="#00D4FF" radius={[0,4,4,0]} name="Deliveries" />
                </BarChart>
              </ResponsiveContainer>
          }
        </div>
      </div>
    </div>
  );
}
